import axiosClient from "utils/axios";
import { ApiResponse } from "types/api.types";
import {
  CancelComplaintMaterialPayload,
  ComplaintMaterial,
  ComplaintMaterialListParams,
  ComplaintMaterialsTableResponse,
  CreateComplaintMaterialPayload,
  UpdateComplaintMaterialPayload,
} from "features/ComplaintMaterials/types/complaint-material.types";

const COMPLAINT_MATERIAL_BASE_PATH = "/complaint-materials";

function buildComplaintMaterialListQueryParams(
  input: ComplaintMaterialListParams,
): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  const setNonEmpty = (key: string, v: string | undefined) => {
    if (v != null && String(v).trim() !== "") {
      params[key] = String(v).trim();
    }
  };
  const setPositiveInt = (key: string, v: number | undefined) => {
    if (v != null && Number.isFinite(v) && Number(v) > 0) {
      params[key] = Number(v);
    }
  };

  if (input.page != null) params.page = input.page;
  if (input.limit != null) params.limit = input.limit;
  setNonEmpty("search", input.search);
  setNonEmpty("status", input.status);
  setPositiveInt("clientId", input.clientId);
  setNonEmpty("warrantyStatus", input.warrantyStatus);

  return params;
}

export const getComplaintMaterials = async (
  input: ComplaintMaterialListParams = {},
) => {
  try {
    const response = await axiosClient.get<
      ApiResponse<ComplaintMaterialsTableResponse>
    >(COMPLAINT_MATERIAL_BASE_PATH, {
      params: buildComplaintMaterialListQueryParams(input),
    });
    return response.data;
  } catch (err) {
    console.warn("Error fetching complaint materials : ", err);
    throw err;
  }
};

export const getComplaintMaterial = async (id: number) => {
  try {
    const response = await axiosClient.get<ApiResponse<ComplaintMaterial>>(
      `${COMPLAINT_MATERIAL_BASE_PATH}/${id}`,
    );
    return response.data;
  } catch (err) {
    console.warn("Error fetching complaint material : ", err);
    throw err;
  }
};

export const createComplaintMaterial = async (
  payload: CreateComplaintMaterialPayload,
) => {
  try {
    const response = await axiosClient.post<ApiResponse<ComplaintMaterial>>(
      COMPLAINT_MATERIAL_BASE_PATH,
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error creating complaint material : ", err);
    throw err;
  }
};

export const updateComplaintMaterial = async (
  id: number,
  payload: UpdateComplaintMaterialPayload,
) => {
  try {
    const response = await axiosClient.put<ApiResponse<ComplaintMaterial>>(
      `${COMPLAINT_MATERIAL_BASE_PATH}/${id}`,
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error updating complaint material : ", err);
    throw err;
  }
};

export const deleteComplaintMaterial = async (id: number) => {
  try {
    const response = await axiosClient.delete<ApiResponse<ComplaintMaterial>>(
      `${COMPLAINT_MATERIAL_BASE_PATH}/${id}`,
    );
    return response.data;
  } catch (err) {
    console.warn("Error deleting complaint material : ", err);
    throw err;
  }
};

/**
 * The one surviving action — an override outside the date register.
 * Rejected if already CANCELLED or already HANDED_BACK_TO_CLIENT.
 */
export const cancelComplaintMaterial = async (
  id: number,
  payload?: CancelComplaintMaterialPayload,
) => {
  try {
    const response = await axiosClient.post<ApiResponse<ComplaintMaterial>>(
      `${COMPLAINT_MATERIAL_BASE_PATH}/${id}/cancel`,
      payload ?? {},
    );
    return response.data;
  } catch (err) {
    console.warn("Error cancelling complaint material : ", err);
    throw err;
  }
};
